import axios from "axios";
import { isAbortError, isAxiosClientTimeout } from "../../utils/api";
import {
  describeAxiosError,
  type DiagnosticProps,
} from "../../utils/diagnostics";

interface DevicesLoadErrorConfig {
  title: string;
  message: string;
  autoRetry: boolean;
}

export const DEVICES_LOAD_ERRORS = {
  NETWORK_OFFLINE: {
    title: "Sin conexión a internet",
    message: "Revisa tu conexión a internet e intenta de nuevo.",
    autoRetry: true,
  },
  API_UNREACHABLE: {
    title: "Error de conexión",
    message:
      "No pudimos comunicarnos con el servidor. Intenta de nuevo en unos momentos.",
    autoRetry: true,
  },
  API_TIMEOUT: {
    title: "Error de conexión",
    message: "El servidor tardó demasiado en responder. Intenta de nuevo.",
    autoRetry: true,
  },
  API_SERVER_ERROR: {
    title: "Error de conexión",
    message:
      "El servidor tuvo un problema al obtener las unidades. Intenta de nuevo en unos momentos.",
    autoRetry: true,
  },
  API_HTTP_ERROR: {
    title: "Error al obtener las unidades",
    message:
      "El servidor rechazó la solicitud de unidades. Si el problema persiste, contacta al soporte técnico.",
    autoRetry: false,
  },
  API_BAD_SHAPE: {
    title: "Error al obtener las unidades",
    message:
      "El servidor respondió con datos inesperados. Intenta de nuevo en unos momentos.",
    autoRetry: true,
  },
  COMPANY_SCOPE_FORBIDDEN: {
    title: "Sin acceso a la compañía",
    message:
      "No tienes acceso a la compañía seleccionada. Cierra sesión y vuelve a entrar.",
    autoRetry: false,
  },
  SOCKET_GAVE_UP: {
    title: "Conexión en tiempo real perdida",
    message:
      "Las posiciones de las unidades dejaron de actualizarse. Intenta reconectar.",
    autoRetry: false,
  },
  UNEXPECTED: {
    title: "Error al obtener las unidades",
    message: "Ocurrió un error inesperado. Intenta de nuevo.",
    autoRetry: false,
  },
} as const satisfies Record<string, DevicesLoadErrorConfig>;

export type DevicesLoadErrorCode = keyof typeof DEVICES_LOAD_ERRORS;

export class DevicesLoadError extends Error {
  readonly code: DevicesLoadErrorCode;
  readonly details: DiagnosticProps;

  constructor(code: DevicesLoadErrorCode, details: DiagnosticProps = {}) {
    super(code);
    this.name = "DevicesLoadError";
    this.code = code;
    this.details = details;
  }
}

export const classifyDevicesError = (
  error: unknown,
): DevicesLoadError | null => {
  if (isAbortError(error)) return null;
  if (error instanceof DevicesLoadError) return error;

  const details = describeAxiosError(error);
  if (!axios.isAxiosError(error)) {
    return new DevicesLoadError("UNEXPECTED", details);
  }
  if (isAxiosClientTimeout(error)) {
    return new DevicesLoadError("API_TIMEOUT", details);
  }
  if (!error.response) {
    return new DevicesLoadError(
      navigator.onLine ? "API_UNREACHABLE" : "NETWORK_OFFLINE",
      details,
    );
  }
  if (details.message_key === "COMPANY_SCOPE_FORBIDDEN") {
    return new DevicesLoadError("COMPANY_SCOPE_FORBIDDEN", details);
  }
  if (error.response.status >= 500) {
    return new DevicesLoadError("API_SERVER_ERROR", details);
  }
  return new DevicesLoadError("API_HTTP_ERROR", details);
};

export const summarizeDeviceCompanies = (
  devices: { companyId?: string }[],
): DiagnosticProps => {
  const counts = devices.reduce<Map<string, number>>((acc, device) => {
    const key = device.companyId || "sin_compania";
    acc.set(key, (acc.get(key) ?? 0) + 1);
    return acc;
  }, new Map());
  return {
    device_companies_count: counts.size,
    device_companies_top: [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id, count]) => `${id}:${count}`)
      .join(","),
  };
};
